import { type UIGarage } from '@parkingbase/ecommerce-api-test';

import { create } from 'zustand';
import { GarageService } from '@/services/garage.service';
import { logger } from '@/lib/logger';

interface UIGaragesState {
  uiGarages: UIGarage[];
  isLoading: boolean;
  hasFetched: boolean;
  fetchUIGarages: () => Promise<void>;
  getUIGarageById: (garageId: string) => UIGarage | undefined;
}

// Module-level singleton — avoids constructing a new GarageService (and its
// underlying GarageApi + axios config) on every fetch call.
const garageService = new GarageService();

/**
 * Holds the per-garage gateway facts (`UIGarage[]`) fetched once per
 * authenticated session from the auth-gated `/garages/forEcommerce` endpoint.
 * Read by payment surfaces that need per-garage gateway resolution (BOEC-10276
 * multi-payment-gateway).
 *
 * The list is intentionally small — id, name, gatewayType, mode, domain,
 * gatewayConfigurationId — so we keep it in a dedicated store rather than
 * weighing the existing reserve / payment stores down.
 */
export const useUIGaragesStore = create<UIGaragesState>((set, get) => ({
  uiGarages: [],
  isLoading: false,
  hasFetched: false,
  fetchUIGarages: async () => {
    // On error, hasFetched stays false — a later call (or companyId change)
    // can retry; payment surfaces fall back to theme-level resolution.
    if (get().isLoading || get().hasFetched) return;
    try {
      set({ isLoading: true });
      const uiGarages = await garageService.getUIGarages();
      set({ uiGarages, hasFetched: true });
    } catch (error) {
      logger.error('Failed to fetch UI garages', error, {
        feature: 'ui-garages-store',
        route: 'fetchUIGarages',
      });
    } finally {
      set({ isLoading: false });
    }
  },
  getUIGarageById: (garageId: string) => {
    return get().uiGarages.find((g) => g.id === garageId);
  },
}));
